import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Check, Search } from "lucide-react";
import { useCountry } from "@/contexts/CountryContext";
import { useAuthModal } from "@/contexts/AuthModalContext";
import { countries } from "@/data/countries";
import { CountryFlag } from "@/components/ui/CountryFlag";
import { setReturnTo } from "@/lib/return-to";
import { track } from "@/lib/analytics";
import { C } from "./parts";

/*
 * The first question of the plan, "Where are you moving from?", shared by the
 * homepage hero (PlanLauncher) and /start (PlanFlow), so both ask it the same
 * way: the student's own country as one big button, six common origins, a
 * search for the rest.
 */

/**
 * The one-tap flags under the suggestion: Austria's most common origins for new
 * student permits (Eurostat, 2025), mixed across regions on purpose, so the
 * list never says who Migrada is for. Six show; the rest fill in when one is
 * already the suggestion.
 */
const COMMON = ["RU", "IN", "TR", "US", "CN", "KZ", "IR", "PK"];

export interface Guess {
  code: string;
  from: "page" | "connection";
}

/** Austria and countries we do not cover are never suggested. */
const covered = (c: string | null | undefined): c is string => !!c && c !== "AT" && countries.some((x) => x.code === c);

/**
 * The suggestion, "Moving from India?": the country page they came from, else
 * the country Cloudflare sees for this visit (functions/geo.js). Only a
 * suggestion: never stored, never an answer on its own. No answer within 1.2
 * seconds: the list alone. Already answered: no lookup, their answer is the
 * big button.
 *
 * Testing from Vienna (always AT, never suggested): on any host but
 * migrada.eu, `?geo=IN` stands in for the visit's country.
 */
export function useCountryGuess(answered: boolean) {
  const { selectedCode } = useCountry();
  const [guess, setGuess] = useState<Guess | null>(() => (covered(selectedCode) ? { code: selectedCode, from: "page" } : null));
  const [guessing, setGuessing] = useState(() => !covered(selectedCode) && !answered);

  useEffect(() => {
    if (!guessing) return;
    const test = window.location.hostname === "migrada.eu" ? null : new URLSearchParams(window.location.search).get("geo")?.toUpperCase();
    if (test) {
      if (covered(test)) setGuess({ code: test, from: "connection" });
      setGuessing(false);
      return;
    }
    const ctl = new AbortController();
    const stop = window.setTimeout(() => ctl.abort(), 1200);
    fetch("/geo", { cache: "no-store", signal: ctl.signal })
      .then((r) => (r.ok && (r.headers.get("content-type") ?? "").includes("json") ? r.json() : null))
      .then((d: { country?: string | null } | null) => {
        if (d && covered(d.country)) setGuess({ code: d.country, from: "connection" });
      })
      .catch(() => undefined)
      .finally(() => {
        window.clearTimeout(stop);
        setGuessing(false);
      });
    return () => {
      ctl.abort();
      window.clearTimeout(stop);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const shownFor = useRef<string | null>(null);
  useEffect(() => {
    if (!guess || shownFor.current === guess.code) return;
    shownFor.current = guess.code;
    track("plan_country_suggested", { country: guess.code, from: guess.from, action: "shown" });
  }, [guess]);

  return { guess, guessing };
}

export function CountryStep({
  code,
  guess,
  guessing,
  account,
  onChoose,
  narrow = false,
}: {
  code: string | null;
  guess: Guess | null;
  guessing: boolean;
  account: string | null;
  onChoose: (code: string) => void;
  /** Two flags a row at every width (the hero card is too narrow for three). */
  narrow?: boolean;
}) {
  const { openSignIn } = useAuthModal();
  const [searching, setSearching] = useState(false);
  const [query, setQuery] = useState("");

  // Back on this question later: their own answer is the big button.
  const suggestion = guess ?? (code ? { code, from: "answer" as const } : null);
  const suggested = suggestion ? countries.find((c) => c.code === suggestion.code) : undefined;
  const country = code ? countries.find((c) => c.code === code) : undefined;
  // A browser set to Russian gets one line to the Russian page (/ru), never a redirect.
  const russian = /^ru\b/i.test(typeof navigator !== "undefined" ? navigator.language ?? "" : "");

  const matches = useMemo(() => {
    const q = query.trim().toLowerCase();
    return countries.filter((c) => !q || c.name.toLowerCase().includes(q) || c.code.toLowerCase() === q).sort((a, b) => a.name.localeCompare(b.name));
  }, [query]);
  const popular = useMemo(
    () =>
      COMMON.filter((c) => c !== suggestion?.code)
        .slice(0, 6)
        .map((c) => countries.find((x) => x.code === c))
        .filter((c): c is (typeof countries)[number] => !!c),
    [suggestion?.code]
  );

  const choose = (c: string) => {
    setSearching(false);
    setQuery("");
    onChoose(c);
  };

  return (
    <>
      {russian && (
        <Link to="/ru" lang="ru" className="mt-2 inline-flex items-center gap-1.5 text-[15px] font-bold underline-offset-4 hover:underline" style={{ color: C.tealDeep }}>
          Есть версия на русском
          <ArrowRight className="h-4 w-4" aria-hidden="true" />
        </Link>
      )}
      {!searching ? (
        <>
          {guessing ? (
            <div className="mt-6 h-[88px] animate-pulse rounded-[24px]" style={{ backgroundColor: C.field }} aria-hidden="true" />
          ) : (
            suggestion &&
            suggested && (
              <button
                type="button"
                onClick={() => {
                  if (guess && suggestion.code === guess.code) track("plan_country_suggested", { country: guess.code, from: guess.from, action: "used" });
                  choose(suggestion.code);
                }}
                aria-pressed={code === suggestion.code}
                className="mt-6 flex w-full items-center gap-4 rounded-[24px] border-2 p-4 text-left transition-[background-color,border-color,transform] active:scale-[0.98] sm:p-5"
                style={{ backgroundColor: code === suggestion.code ? C.tealSoft : "#F3FCFA", borderColor: code === suggestion.code ? C.tealDeep : "#BFEDE6" }}
              >
                <CountryFlag code={suggestion.code} size="lg" circle animate={false} />
                <span className="min-w-0 flex-1">
                  <span className="block text-[13px] font-bold" style={{ color: C.muted }}>
                    Moving from
                  </span>
                  <span className="block truncate text-[22px] font-black leading-tight tracking-[-0.02em]" style={{ color: C.brand }}>
                    {suggested.name}?
                  </span>
                </span>
                <span className="inline-flex h-11 shrink-0 items-center gap-1.5 rounded-full px-5 text-[15px] font-bold" style={{ backgroundColor: C.teal, color: C.brand }}>
                  Yes
                  <ArrowRight className="h-4 w-4" aria-hidden="true" />
                </span>
              </button>
            )
          )}
          {suggestion && !guessing && (
            <p className="mt-5 text-[13px] font-bold" style={{ color: C.muted }}>
              Somewhere else?
            </p>
          )}
          <div className={`${suggestion && !guessing ? "mt-2.5" : "mt-6"} grid grid-cols-2 gap-2.5 ${narrow ? "" : "sm:grid-cols-3"}`}>
            {popular.map((c) => {
              const on = code === c.code;
              return (
                <button
                  key={c.code}
                  type="button"
                  onClick={() => choose(c.code)}
                  aria-pressed={on}
                  className="flex min-h-[58px] items-center gap-2.5 rounded-2xl border-2 px-3 text-left text-[14px] font-bold transition-[background-color,border-color,transform] active:scale-[0.97] sm:text-[15px]"
                  style={{ backgroundColor: on ? C.tealSoft : "#FFFFFF", borderColor: on ? C.tealDeep : C.rule, color: C.brand }}
                >
                  <CountryFlag code={c.code} size="md" circle animate={false} />
                  {/* Wraps rather than cuts: "United States" on two lines reads better than "United S…". */}
                  <span className="min-w-0 leading-[1.15] line-clamp-2">{c.name}</span>
                </button>
              );
            })}
          </div>
          <button
            type="button"
            onClick={() => setSearching(true)}
            className="mt-2.5 flex min-h-[58px] w-full items-center gap-3 rounded-2xl border-2 border-dashed px-4 text-[15px] font-bold transition-colors hover:bg-[#F7F7F3]"
            style={{ borderColor: C.rule, color: C.brand }}
          >
            <Search className="h-4 w-4" aria-hidden="true" />
            {code && code !== suggestion?.code && !popular.some((c) => c.code === code) && country ? (
              <>
                <CountryFlag code={country.code} size="sm" circle animate={false} />
                {country.name}
              </>
            ) : (
              "Another country"
            )}
          </button>
        </>
      ) : (
        <div className="mt-6">
          <div className="flex items-center gap-2 rounded-2xl border-2 px-4" style={{ borderColor: C.tealDeep }}>
            <Search className="h-4 w-4 shrink-0" style={{ color: C.muted }} aria-hidden="true" />
            <input
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => {
                if (e.key !== "Enter") return;
                e.preventDefault();
                if (matches[0]) choose(matches[0].code);
              }}
              placeholder="Type your country"
              aria-label="Search countries"
              className="h-[54px] w-full bg-transparent text-[16px] font-bold outline-none"
              style={{ color: C.ink }}
            />
          </div>
          <div className="mt-2 max-h-[264px] overflow-y-auto rounded-2xl border" style={{ borderColor: C.rule }} role="listbox" aria-label="Countries">
            {matches.map((c) => (
              <button
                key={c.code}
                type="button"
                role="option"
                aria-selected={code === c.code}
                onClick={() => choose(c.code)}
                className="flex w-full items-center gap-3 px-4 py-2.5 text-left text-[15px] transition-colors hover:bg-[rgba(10,15,14,0.04)]"
                style={{ color: C.ink }}
              >
                <CountryFlag code={c.code} size="sm" circle animate={false} />
                <span className="truncate">{c.name}</span>
                {code === c.code && <Check className="ml-auto h-4 w-4 shrink-0" style={{ color: C.tealDeep }} aria-hidden="true" />}
              </button>
            ))}
            {matches.length === 0 && (
              <p className="px-4 py-4 text-[15px]" style={{ color: C.muted }}>
                No country matches “{query}”.
              </p>
            )}
          </div>
          <button type="button" onClick={() => setSearching(false)} className="mt-3 text-[14px] font-bold underline underline-offset-4" style={{ color: C.brand }}>
            Back to the list
          </button>
        </div>
      )}
      <p className="mt-5 text-[13px] leading-snug" style={{ color: C.muted }}>
        EU, EEA or Swiss passport? You need no student permit.{" "}
        {!account && (
          <button
            type="button"
            onClick={() => {
              setReturnTo("/app");
              openSignIn();
            }}
            className="font-bold underline underline-offset-2"
            style={{ color: C.brand }}
          >
            Already with us? Sign in
          </button>
        )}
      </p>
    </>
  );
}
